import { chooseAiAction, explainAction } from "./ai";
import type { AiWorkerRequest, AiWorkerResponse, AiWorkerResult } from "./ai.worker";
import type { Difficulty, PublicView } from "./types";

export type { AiWorkerResult };

// Minimal structural view of a Web Worker so the bridge state machine can be
// driven by a fake worker in vitest (node) without a DOM.
export interface WorkerLike {
  onmessage: ((event: MessageEvent<AiWorkerResponse>) => void) | null;
  onerror: ((event: unknown) => void) | null;
  postMessage: (message: AiWorkerRequest) => void;
  terminate: () => void;
}

export type WorkerFactory = () => WorkerLike;

export interface AiBridgeOptions {
  /** Max worker think time before falling back to the main thread. */
  timeoutMs?: number;
  /** Injected in tests; defaults to the real Vite-bundled module worker. */
  createWorker?: WorkerFactory;
}

export interface AiBridge {
  request: (view: PublicView, difficulty: Difficulty) => Promise<AiWorkerResult>;
  /** Voids the in-flight request; its late response is dropped, its promise stays unsettled. */
  cancel: () => void;
  /** Terminates the worker and voids the in-flight request. */
  destroy: () => void;
}

const DEFAULT_TIMEOUT_MS = 1500;

function defaultFactory(): WorkerLike {
  if (typeof Worker === "undefined") throw new Error("Web Workers are unavailable");
  return new Worker(new URL("./ai.worker.ts", import.meta.url), { type: "module" }) as unknown as WorkerLike;
}

// The fallback runs the exact same pure functions the worker runs, so a
// degraded decision is identical to a worker decision on the same input.
function decideLocally(view: PublicView, difficulty: Difficulty): AiWorkerResult {
  const action = chooseAiAction(view, difficulty);
  return { action, explanation: explainAction(view, action, difficulty) };
}

export function createAiBridge(options: AiBridgeOptions = {}): AiBridge {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  let worker: WorkerLike | null = null;
  let workerDead = false;
  let nextId = 0;
  let pending: { id: number; timer: ReturnType<typeof setTimeout> } | null = null;

  function ensureWorker(): WorkerLike | null {
    if (workerDead) return null;
    if (worker) return worker;
    try {
      const created = (options.createWorker ?? defaultFactory)();
      created.onmessage = (event) => {
        const response = event.data;
        if (!pending || response.id !== pending.id) return; // stale response: drop
        clearTimeout(pending.timer);
        pending = null;
        settle(response.id, response.result);
      };
      created.onerror = () => {
        // Permanent degradation: every later request runs on the main thread.
        workerDead = true;
        try {
          created.terminate();
        } catch {
          // Termination is best-effort.
        }
        worker = null;
        if (pending) {
          clearTimeout(pending.timer);
          const id = pending.id;
          pending = null;
          settle(id, null);
        }
      };
      worker = created;
      return worker;
    } catch {
      workerDead = true;
      return null;
    }
  }

  // Resolvers are stored per request id so a voided request never settles.
  const resolvers = new Map<number, (result: AiWorkerResult) => void>();
  const contexts = new Map<number, { view: PublicView; difficulty: Difficulty }>();

  function settle(id: number, result: AiWorkerResult | null): void {
    const resolve = resolvers.get(id);
    const context = contexts.get(id);
    resolvers.delete(id);
    contexts.delete(id);
    if (!resolve || !context) return;
    resolve(result ?? decideLocally(context.view, context.difficulty));
  }

  function cancel(): void {
    if (pending) {
      clearTimeout(pending.timer);
      // The promise is intentionally left unsettled and its late response will
      // be dropped by the id check; the caller has abandoned this turn.
      resolvers.delete(pending.id);
      contexts.delete(pending.id);
      pending = null;
    }
  }

  function request(view: PublicView, difficulty: Difficulty): Promise<AiWorkerResult> {
    cancel();
    const id = ++nextId;
    const instance = ensureWorker();
    if (!instance) return Promise.resolve(decideLocally(view, difficulty));
    return new Promise<AiWorkerResult>((resolve) => {
      resolvers.set(id, resolve);
      contexts.set(id, { view, difficulty });
      const timer = setTimeout(() => {
        if (!pending || pending.id !== id) return;
        pending = null;
        // The worker may be stuck; recycle it and degrade this request only.
        try {
          instance.terminate();
        } catch {
          // Termination is best-effort.
        }
        if (worker === instance) worker = null;
        settle(id, null);
      }, timeoutMs);
      pending = { id, timer };
      try {
        instance.postMessage({ id, view, difficulty });
      } catch {
        clearTimeout(timer);
        pending = null;
        workerDead = true;
        worker = null;
        settle(id, null);
      }
    });
  }

  function destroy(): void {
    cancel();
    if (worker) {
      try {
        worker.terminate();
      } catch {
        // Termination is best-effort.
      }
      worker = null;
    }
  }

  return { request, cancel, destroy };
}
